import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { accountService } from '@/features/account/api/accountService'
import { accountKeys } from '@/features/account/api/keys'
import type { Me, MemberProfile, Preferences } from '@/schemas/account'

/**
 * Identity for the chrome — name, plan, avatar.
 *
 * `staleTime: Infinity` because a member's name and plan cannot change without
 * an action this app performs. The chrome mounts on every route, so the client
 * default of 30s would refetch on every window focus for data that only ever
 * changes through a mutation that can invalidate `accountKeys.all` itself.
 */
export const meQueryOptions = () =>
  queryOptions<Me>({
    queryKey: accountKeys.me(),
    queryFn: () => accountService.me(),
    staleTime: Infinity,
  })

/**
 * The chrome's own read — `ProfileMenu`, `SidebarMemberCard` and Portfolio's
 * `HealthScreen` all mount alongside `AppShell` rather than behind a route
 * loader, so they keep the plain hook. It shares this factory's key with
 * `useSuspenseMe`, which is what lets Profile and Subscription's loaders
 * `ensureRouteData` this exact entry instead of firing a second request: by
 * the time either route is entered, the chrome has almost always already
 * cached it (`staleTime: Infinity`, so it is never too stale to reuse).
 */
export const useMe = () => useQuery(meQueryOptions())

/**
 * Profile and Subscription's read of it — both routes' loader awaits this
 * exact key, so it is always in cache by the time either mounts.
 */
export const useSuspenseMe = (): UseSuspenseQueryResult<Me> => useSuspenseQuery(meQueryOptions())

export const memberProfileQueryOptions = () =>
  queryOptions<MemberProfile>({
    queryKey: accountKeys.profile(),
    queryFn: () => accountService.profile(),
    staleTime: Infinity,
  })

/**
 * Settings' own read. Kept as a plain query — not suspense — because only the
 * `edit-profile` tab needs it and a member can reach that tab through a
 * search-only change (`?tab=` on the settings route already mounted), which
 * re-runs no loader: see `SettingsScreen.loader.ts`. The route's loader still
 * `ensureRouteData`s this same key for the entry URL, so a fresh navigation
 * to `/settings?tab=edit-profile` never shows this hook's own loading branch.
 */
export const useMemberProfile = () => useQuery(memberProfileQueryOptions())

/** Profile's read — its loader awaits this exact key. */
export const useSuspenseMemberProfile = (): UseSuspenseQueryResult<MemberProfile> =>
  useSuspenseQuery(memberProfileQueryOptions())

/**
 * The member's served settings — the language company summaries come in. `staleTime: Infinity`
 * for the reason `meQueryOptions` gives: only this app's own PATCH changes it, and that writes the
 * cache itself.
 */
export const preferencesQueryOptions = () =>
  queryOptions<Preferences>({
    queryKey: accountKeys.preferences(),
    queryFn: () => accountService.preferences(),
    staleTime: Infinity,
  })

export const usePreferences = () => useQuery(preferencesQueryOptions())
